// Statistical data from U.S. Census Bureau, CDC NHANES, BLS, and Pew Research Center
// All data represents the most recent available statistics for U.S. men aged 18-44 as of 2024

export interface MaleDemographicData {
  population: {
    total_men_18_44: number;
    by_race: Record<string, number>;
  };
  age_distribution: Record<string, Record<string, number>>;
  height_at_least: Record<string, number>;
  income_at_least_by_age: Record<string, Record<string, number>>;
  political_affiliation: Record<string, Record<string, number>>;
  bmi_distribution: Record<string, Record<string, number>>;
  marital_status: Record<string, {
    never_married: Record<string, number>;
    divorced_separated: Record<string, number>;
  }>;
  educational_attainment: Record<string, {
    high_school_or_less: Record<string, number>;
    some_college: Record<string, number>;
    bachelors_degree: Record<string, number>;
    graduate_degree: Record<string, number>;
  }>;
  employment: {
    employed_by_age: Record<string, number>;
  };
}

export const maleDemographicData: MaleDemographicData = {
  // U.S. Census Bureau American Community Survey 2022
  population: {
    total_men_18_44: 55700000, // Approximate total U.S. men aged 18-44
    by_race: {
      all: 1.0,
      white: 0.58,      // White, Non-Hispanic
      black: 0.13,      // Black or African American
      hispanic: 0.20,   // Hispanic or Latino (any race)
      asian: 0.06,      // Asian
      other: 0.03       // Other races
    }
  },

  // Age distribution within 18-44 range by race (Census ACS 2022)
  age_distribution: {
    all: { '18-24': 0.26, '25-34': 0.38, '35-44': 0.36 },
    white: { '18-24': 0.24, '25-34': 0.36, '35-44': 0.40 },
    black: { '18-24': 0.29, '25-34': 0.39, '35-44': 0.32 },
    hispanic: { '18-24': 0.30, '25-34': 0.42, '35-44': 0.28 },
    asian: { '18-24': 0.22, '25-34': 0.45, '35-44': 0.33 }
  },

  // Cumulative share of men at or above each height threshold
  // (CDC NHANES Anthropometric Reference Data 2015-2018; U.S. adult male mean height ~5'9", SD ~3")
  height_at_least: {
    '5-8': 0.60,   // 5'8" or taller
    '5-10': 0.35,  // 5'10" or taller
    '6-0': 0.145,  // 6'0" or taller
    '6-2': 0.04,   // 6'2" or taller
    '6-4': 0.01    // 6'4" or taller
  },

  // Share of men earning at least each annual individual income threshold, by age group
  // (Census PINC/ACS income tables 2022; includes all men, working or not)
  income_at_least_by_age: {
    '50k': { '18-24': 0.12, '25-34': 0.42, '35-44': 0.55 },   // $50,000+
    '75k': { '18-24': 0.04, '25-34': 0.22, '35-44': 0.33 },   // $75,000+
    '100k': { '18-24': 0.015, '25-34': 0.12, '35-44': 0.20 }, // $100,000+
    '200k': { '18-24': 0.002, '25-34': 0.02, '35-44': 0.05 }  // $200,000+
  },

  // Political affiliation by race (Pew Research Center 2023) - men skew more conservative than women
  political_affiliation: {
    all: { conservative: 0.38, moderate: 0.36, liberal: 0.26 },
    white: { conservative: 0.45, moderate: 0.33, liberal: 0.22 },
    black: { conservative: 0.20, moderate: 0.35, liberal: 0.45 },
    hispanic: { conservative: 0.30, moderate: 0.42, liberal: 0.28 },
    asian: { conservative: 0.25, moderate: 0.38, liberal: 0.37 }
  },

  // BMI distribution - percentage of men with BMI < 30 (not obese) by race (CDC NHANES 2017-2020)
  bmi_distribution: {
    all: { not_obese: 0.60 },      // BMI < 30
    white: { not_obese: 0.59 },
    black: { not_obese: 0.60 },
    hispanic: { not_obese: 0.53 },
    asian: { not_obese: 0.83 }
  },

  // Marital status by race and age (U.S. Census ACS 2022, Table B12002)
  // Men marry later than women, so never-married shares are higher at every age
  marital_status: {
    all: {
      never_married: { '18-24': 0.93, '25-34': 0.58, '35-44': 0.27 },
      divorced_separated: { '18-24': 0.01, '25-34': 0.08, '35-44': 0.18 }
    },
    white: {
      never_married: { '18-24': 0.94, '25-34': 0.55, '35-44': 0.23 },
      divorced_separated: { '18-24': 0.01, '25-34': 0.08, '35-44': 0.18 }
    },
    black: {
      never_married: { '18-24': 0.96, '25-34': 0.75, '35-44': 0.50 },
      divorced_separated: { '18-24': 0.01, '25-34': 0.08, '35-44': 0.20 }
    },
    hispanic: {
      never_married: { '18-24': 0.91, '25-34': 0.52, '35-44': 0.28 },
      divorced_separated: { '18-24': 0.01, '25-34': 0.09, '35-44': 0.19 }
    },
    asian: {
      never_married: { '18-24': 0.97, '25-34': 0.60, '35-44': 0.25 },
      divorced_separated: { '18-24': 0.005, '25-34': 0.05, '35-44': 0.12 }
    }
  },

  // Educational attainment by race and age (U.S. Census Bureau Educational Attainment Tables B15002)
  // Men have lower college attainment than women; shares per race/age group sum to ~1.0
  educational_attainment: {
    all: {
      high_school_or_less: { '18-24': 0.45, '25-34': 0.35, '35-44': 0.38 },
      some_college: { '18-24': 0.38, '25-34': 0.28, '35-44': 0.28 },
      bachelors_degree: { '18-24': 0.14, '25-34': 0.27, '35-44': 0.23 },
      graduate_degree: { '18-24': 0.03, '25-34': 0.10, '35-44': 0.11 }
    },
    white: {
      high_school_or_less: { '18-24': 0.40, '25-34': 0.31, '35-44': 0.33 },
      some_college: { '18-24': 0.38, '25-34': 0.27, '35-44': 0.28 },
      bachelors_degree: { '18-24': 0.18, '25-34': 0.30, '35-44': 0.27 },
      graduate_degree: { '18-24': 0.04, '25-34': 0.12, '35-44': 0.12 }
    },
    black: {
      high_school_or_less: { '18-24': 0.55, '25-34': 0.45, '35-44': 0.48 },
      some_college: { '18-24': 0.36, '25-34': 0.33, '35-44': 0.32 },
      bachelors_degree: { '18-24': 0.08, '25-34': 0.17, '35-44': 0.15 },
      graduate_degree: { '18-24': 0.01, '25-34': 0.05, '35-44': 0.05 }
    },
    hispanic: {
      high_school_or_less: { '18-24': 0.58, '25-34': 0.50, '35-44': 0.55 },
      some_college: { '18-24': 0.32, '25-34': 0.28, '35-44': 0.26 },
      bachelors_degree: { '18-24': 0.09, '25-34': 0.17, '35-44': 0.14 },
      graduate_degree: { '18-24': 0.01, '25-34': 0.05, '35-44': 0.05 }
    },
    asian: {
      high_school_or_less: { '18-24': 0.18, '25-34': 0.13, '35-44': 0.16 },
      some_college: { '18-24': 0.28, '25-34': 0.17, '35-44': 0.18 },
      bachelors_degree: { '18-24': 0.42, '25-34': 0.45, '35-44': 0.40 },
      graduate_degree: { '18-24': 0.12, '25-34': 0.25, '35-44': 0.26 }
    }
  },

  // Employment - share of men employed full- or part-time by age group (BLS/ACS 2023)
  employment: {
    employed_by_age: {
      '18-24': 0.68, // Lower due to school enrollment
      '25-34': 0.86,
      '35-44': 0.88
    }
  }
};

// Data source citations
export const maleDataSources = [
  {
    id: 1,
    title: "U.S. Census Bureau American Community Survey 2022",
    url: "https://www.census.gov/programs-surveys/acs/",
    description: "Annual demographic data including age, race, ethnicity, and population counts for U.S. men"
  },
  {
    id: 2,
    title: "CDC NHANES Anthropometric Reference Data 2015-2018",
    url: "https://www.cdc.gov/nchs/nhanes/",
    description: "Measured height, weight, and BMI distributions for U.S. adult men by demographic group"
  },
  {
    id: 3,
    title: "U.S. Census Bureau Personal Income Tables (PINC) / ACS Income Data",
    url: "https://www.census.gov/data/tables/time-series/demo/income-poverty/cps-pinc.html",
    description: "Individual annual income distributions for men by age group"
  },
  {
    id: 4,
    title: "Pew Research Center Political Typology 2023",
    url: "https://www.pewresearch.org/politics/",
    description: "Political affiliation and ideology surveys by sex, race, and demographic characteristics"
  },
  {
    id: 5,
    title: "U.S. Census Bureau Marital Status Tables (B12002)",
    url: "https://data.census.gov/table/ACSDT1Y2022.B12002",
    description: "Detailed marital status data by age, sex, and race/ethnicity"
  },
  {
    id: 6,
    title: "U.S. Census Bureau Educational Attainment Tables (B15002)",
    url: "https://data.census.gov/table/ACSDT1Y2022.B15002",
    description: "Educational attainment by sex, race, ethnicity, and age from American Community Survey"
  },
  {
    id: 7,
    title: "CDC National Survey of Family Growth (NSFG)",
    url: "https://www.cdc.gov/nchs/nsfg/",
    description: "Relationship, marriage, and family formation data for U.S. men by demographic characteristics"
  }
];
